import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import LicensesTab from '@/components/LicensesTab';
import TaskHistory from '@/components/TaskHistory';
import Comments from '@/components/Comments';
import SortableTaskList from '@/components/SortableTaskList';
import TimeEntryModal from '@/components/TimeEntryModal';
import { normalizeProjectStatus } from '@/lib/statusNormalize';
import PhaseBlocksPicker from '@/components/projects/PhaseBlocksPicker';
import RegistrationsTab from '@/components/projects/RegistrationsTab';
import { useRememberLastProject } from '@/lib/lastProject';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { Download, History, FileSpreadsheet, FileText, CalendarDays, MessageSquare } from 'lucide-react';
import logger from '@/lib/logger';

function ProjectDetails({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);

  // Persist as "last visited" for the Home "Continuar de onde parou" shortcut
  useRememberLastProject(project);
  const [phases, setPhases] = useState([]);
  const [tasks, setTasks] = useState({});
  const [loading, setLoading] = useState(true);
  const [openPhaseDialog, setOpenPhaseDialog] = useState(false);
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [phaseForm, setPhaseForm] = useState({ name: '', weight: 0, protocol_date: '' });
  const [phaseBlocksPickerOpen, setPhaseBlocksPickerOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ name: '', weight: 0, deadline: '', assigned_to: '', client_visible: true });
  
  // Edit phase
  const [editPhaseOpen, setEditPhaseOpen] = useState(false);
  const [editPhaseForm, setEditPhaseForm] = useState({});
  const [phaseToEdit, setPhaseToEdit] = useState(null);
  
  // Edit project
  const [editProjectOpen, setEditProjectOpen] = useState(false);
  const [editProjectForm, setEditProjectForm] = useState({});
  
  // Delete project
  const [deleteProjectOpen, setDeleteProjectOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  
  // Edit task
  const [editTaskOpen, setEditTaskOpen] = useState(false);
  const [editTaskForm, setEditTaskForm] = useState({});
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskCommentsFor, setTaskCommentsFor] = useState(null);
  const [taskTimeFor, setTaskTimeFor] = useState(null);
  const [taskTimeSummary, setTaskTimeSummary] = useState({});
  
  // Edit client
  const [editClientOpen, setEditClientOpen] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [editClientForm, setEditClientForm] = useState({});
  
  // Duplicate project
  const [duplicating, setDuplicating] = useState(false);
  
  // Task history
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTask, setHistoryTask] = useState(null);
  
  // Export
  const [exporting, setExporting] = useState(false);
  
  // Delete phase
  const [deletePhaseOpen, setDeletePhaseOpen] = useState(false);
  const [phaseToDelete, setPhaseToDelete] = useState(null);
  
  // Protocol dates management
  const [protocols, setProtocols] = useState([]);
  const [editProtocolOpen, setEditProtocolOpen] = useState(false);
  const [protocolToEdit, setProtocolToEdit] = useState(null);
  const [protocolDateForm, setProtocolDateForm] = useState('');
  const [protocolNumberForm, setProtocolNumberForm] = useState('');
  
  // ARTs management
  const [arts, setArts] = useState([]);
  const [artDialogOpen, setArtDialogOpen] = useState(false);
  const [editingArt, setEditingArt] = useState(null);
  const [artForm, setArtForm] = useState({
    art_number: '',
    activity: '',
    expiration_date: '',
    issued_by: '',
    notes: ''
  });
  
  // Consultants list for assignment
  const [consultants, setConsultants] = useState([]);

  // Recalculate project progress
  const [recalculating, setRecalculating] = useState(false);

  const canEdit = ['ADMIN', 'CONSULTANT'].includes(user?.role?.toUpperCase());
  const isAdmin = user?.role?.toUpperCase() === 'ADMIN';

  const handleRecalculateProject = async () => {
    setRecalculating(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/recalculate`);
      const pct = (data?.overall_percentage ?? 0).toFixed(1);
      toast.success(`Projeto recalculado — progresso atual: ${pct}%`);
      await fetchProjectData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao recalcular projeto');
    } finally {
      setRecalculating(false);
    }
  };

  useEffect(() => {
    fetchProjectData();
    fetchConsultants();
    fetchProtocols();
    fetchArts();
  }, [id]);

  const fetchConsultants = async () => {
    try {
      const response = await api.get('/users/consultants');
      setConsultants(response.data);
    } catch (error) {
      logger.error('Error fetching consultants:', error);
    }
  };

  const fetchProtocols = async () => {
    try {
      const response = await api.get(`/projects/${id}/protocols`);
      setProtocols(response.data);
    } catch (error) {
      logger.error('Error fetching protocols:', error);
    }
  };

  const openEditProtocol = (protocol) => {
    setProtocolToEdit(protocol);
    setProtocolDateForm(protocol.protocol_date || '');
    setProtocolNumberForm(protocol.protocol_number || '');
    setEditProtocolOpen(true);
  };

  const handleUpdateProtocol = async (e) => {
    e.preventDefault();
    if (!protocolToEdit) return;
    
    try {
      await api.put(`/protocols/${protocolToEdit.phase_id}`, { 
        protocol_date: protocolDateForm || null,
        protocol_number: protocolNumberForm || null
      });
      toast.success('Protocolo atualizado com sucesso!');
      setEditProtocolOpen(false);
      setProtocolToEdit(null);
      fetchProtocols();
      fetchProjectData();
    } catch (error) {
      logger.error('Error updating protocol:', error);
      toast.error('Erro ao atualizar protocolo');
    }
  };

  const handleRemoveProtocol = async (phaseId) => {
    if (!window.confirm('Deseja remover a data e número de protocolo desta fase?')) return;
    
    try {
      await api.put(`/protocols/${phaseId}`, { protocol_date: null, protocol_number: null });
      toast.success('Protocolo removido!');
      fetchProtocols();
      fetchProjectData();
    } catch (error) {
      logger.error('Error removing protocol:', error);
      toast.error('Erro ao remover protocolo');
    }
  };

  // ARTs functions
  const fetchArts = async () => {
    try {
      const response = await api.get(`/projects/${id}/arts`);
      setArts(response.data);
    } catch (error) {
      logger.error('Error fetching ARTs:', error);
    }
  };

  const openNewArt = () => {
    setEditingArt(null);
    setArtForm({
      art_number: '',
      activity: '',
      expiration_date: '',
      issued_by: '',
      notes: ''
    });
    setArtDialogOpen(true);
  };

  const openEditArt = (art) => {
    setEditingArt(art);
    setArtForm({
      art_number: art.art_number,
      activity: art.activity,
      expiration_date: art.expiration_date,
      issued_by: art.issued_by || '',
      notes: art.notes || ''
    });
    setArtDialogOpen(true);
  };

  const handleSaveArt = async (e) => {
    e.preventDefault();
    try {
      if (editingArt) {
        await api.put(`/arts/${editingArt.art_id}`, artForm);
        toast.success('ART atualizada com sucesso!');
      } else {
        await api.post(`/projects/${id}/arts`, artForm);
        toast.success('ART criada com sucesso!');
      }
      setArtDialogOpen(false);
      setEditingArt(null);
      fetchArts();
    } catch (error) {
      logger.error('Error saving ART:', error);
      toast.error('Erro ao salvar ART');
    }
  };

  const handleDeleteArt = async (artId) => {
    if (!window.confirm('Deseja excluir esta ART?')) return;
    
    try {
      await api.delete(`/arts/${artId}`);
      toast.success('ART excluída com sucesso!');
      fetchArts();
    } catch (error) {
      logger.error('Error deleting ART:', error);
      toast.error('Erro ao excluir ART');
    }
  };

  const getArtStatusBadge = (status) => {
    switch (status) {
      case 'expired':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">Vencida</span>;
      case 'expiring_soon':
        return <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-700">Vence em breve</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Válida</span>;
    }
  };

  const fetchProjectData = async () => {
    try {
      const [projectRes, phasesRes] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/phases`),
      ]);
      setProject(projectRes.data);
      setPhases(phasesRes.data);

      const tasksData = {};
      for (const phase of phasesRes.data) {
        const tasksRes = await api.get(`/phases/${phase.phase_id}/tasks`);
        tasksData[phase.phase_id] = tasksRes.data;
      }
      setTasks(tasksData);

      // Fetch time summary (aggregate minutes per task_id)
      const allTaskIds = Object.values(tasksData).flat().map((t) => t.task_id);
      if (allTaskIds.length > 0) {
        try {
          const sum = await api.get('/time-entries/summary/by-task', {
            params: { task_ids: allTaskIds.join(',') },
          });
          setTaskTimeSummary(sum.data || {});
        } catch (err) {
          logger.error('Error fetching time summary:', err);
        }
      }
      
      // Fetch client data if admin
      if (isAdmin && projectRes.data.client_id) {
        try {
          const clientRes = await api.get(`/clients/${projectRes.data.client_id}`);
          setClientData(clientRes.data);
        } catch (error) {
          logger.error('Error fetching client:', error);
        }
      }
    } catch (error) {
      logger.error('Error fetching project data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePhase = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${id}/phases`, phaseForm);
      toast.success('Fase criada com sucesso!');
      setOpenPhaseDialog(false);
      setPhaseForm({ name: '', weight: 0 });
      fetchProjectData();
    } catch (error) {
      logger.error('Error creating phase:', error);
      toast.error('Erro ao criar fase');
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/phases/${selectedPhase}/tasks`, taskForm);
      toast.success('Tarefa criada com sucesso!');
      setOpenTaskDialog(false);
      setTaskForm({ name: '', weight: 0, deadline: '', assigned_to: '', client_visible: true });
      fetchProjectData();
    } catch (error) {
      logger.error('Error creating task:', error);
      toast.error('Erro ao criar tarefa');
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      await api.put(`/tasks/${taskId}`, { status: newStatus });
      toast.success('Status atualizado!');
      fetchProjectData();
    } catch (error) {
      logger.error('Error updating task:', error);
      toast.error('Erro ao atualizar status');
    }
  };

  const handleReorderTasks = async (phaseId, newTaskIds) => {
    // Optimistic update so the UI feels instant; rollback on failure.
    const previous = tasks[phaseId] || [];
    const indexById = new Map(newTaskIds.map((id, idx) => [id, idx]));
    const optimistic = [...previous].sort(
      (a, b) => (indexById.get(a.task_id) ?? 0) - (indexById.get(b.task_id) ?? 0),
    );
    setTasks((prev) => ({ ...prev, [phaseId]: optimistic }));
    try {
      await api.put(`/phases/${phaseId}/tasks/reorder`, { task_ids: newTaskIds });
    } catch (error) {
      logger.error('Error reordering tasks:', error);
      toast.error(error.response?.data?.detail || 'Erro ao reordenar tarefas');
      setTasks((prev) => ({ ...prev, [phaseId]: previous }));
    }
  };

  const handleUpdateProjectStatus = async (newStatus) => {
    try {
      await api.put(`/projects/${id}`, { status: newStatus });
      toast.success('Status do projeto atualizado!');
      fetchProjectData();
    } catch (error) {
      logger.error('Error updating project status:', error);
      toast.error('Erro ao atualizar status');
    }
  };

  // Edit project handlers
  const openEditProject = () => {
    setEditProjectForm({
      name: project.name,
      client_name: project.client_name,
      project_type: project.project_type,
      location: project.location,
      start_date: project.start_date,
      estimated_end_date: project.estimated_end_date,
      status: project.status
    });
    setEditProjectOpen(true);
  };

  const handleEditProject = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/projects/${id}`, editProjectForm);
      toast.success('Projeto atualizado com sucesso!');
      setEditProjectOpen(false);
      fetchProjectData();
    } catch (error) {
      logger.error('Error updating project:', error);
      toast.error('Erro ao atualizar projeto');
    }
  };

  // Edit phase handlers
  const openEditPhase = (phase) => {
    setPhaseToEdit(phase);
    setEditPhaseForm({
      name: phase.name,
      weight: phase.weight,
      protocol_date: phase.protocol_date || '',
      active: phase.active !== false
    });
    setEditPhaseOpen(true);
  };

  const handleEditPhase = async (e) => {
    e.preventDefault();
    if (!phaseToEdit) return;
    
    try {
      await api.put(`/phases/${phaseToEdit.phase_id}`, editPhaseForm);
      toast.success('Fase atualizada com sucesso!');
      setEditPhaseOpen(false);
      setPhaseToEdit(null);
      fetchProjectData();
      fetchProtocols();
    } catch (error) {
      logger.error('Error updating phase:', error);
      toast.error('Erro ao atualizar fase');
    }
  };

  // Delete project handlers
  const handleDeleteProject = async () => {
    if (deleteConfirmName !== project.name) {
      toast.error('Nome do projeto não confere');
      return;
    }
    
    try {
      await api.delete(`/projects/${id}?confirm_name=${encodeURIComponent(deleteConfirmName)}`);
      toast.success('Projeto excluído com sucesso!');
      navigate('/projects');
    } catch (error) {
      logger.error('Error deleting project:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir projeto');
    }
  };

  // Edit task handlers
  const openEditTask = (task) => {
    setSelectedTask(task);
    setEditTaskForm({
      name: task.name,
      weight: task.weight,
      deadline: task.deadline || '',
      status: task.status,
      assigned_to: task.assigned_to || '',
      client_visible: task.client_visible ?? true,
      notes: task.notes || ''
    });
    setEditTaskOpen(true);
  };

  const handleEditTask = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/tasks/${selectedTask.task_id}`, editTaskForm);
      toast.success('Tarefa atualizada com sucesso!');
      setEditTaskOpen(false);
      fetchProjectData();
    } catch (error) {
      logger.error('Error updating task:', error);
      toast.error('Erro ao atualizar tarefa');
    }
  };

  const handleCancelTask = async (taskId) => {
    if (!window.confirm('Tem certeza que deseja cancelar esta tarefa?')) return;
    
    try {
      await api.put(`/tasks/${taskId}/cancel`);
      toast.success('Tarefa cancelada com sucesso!');
      fetchProjectData();
    } catch (error) {
      logger.error('Error cancelling task:', error);
      toast.error('Erro ao cancelar tarefa');
    }
  };

  // Edit client handlers
  const openEditClient = () => {
    if (!clientData) return;
    setEditClientForm({
      full_name: clientData.full_name || '',
      email: clientData.email || '',
      phone: clientData.phone || '',
      company_name: clientData.company_name || '',
      contact_person: clientData.contact_person || '',
      notes: clientData.notes || ''
    });
    setEditClientOpen(true);
  };

  const handleEditClient = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/clients/${project.client_id}`, editClientForm);
      toast.success('Cliente atualizado com sucesso!');
      setEditClientOpen(false);
      fetchProjectData();
    } catch (error) {
      logger.error('Error updating client:', error);
      toast.error('Erro ao atualizar cliente');
    }
  };

  // Duplicate project handler
  const handleDuplicateProject = async () => {
    if (!window.confirm('Deseja duplicar este projeto? Todas as fases e tarefas serão copiadas, mas os status das tarefas serão resetados.')) {
      return;
    }
    
    setDuplicating(true);
    try {
      const response = await api.post(`/projects/${id}/duplicate`);
      toast.success('Projeto duplicado com sucesso!');
      navigate(`/projects/${response.data.project_id}`);
    } catch (error) {
      logger.error('Error duplicating project:', error);
      toast.error(error.response?.data?.detail || 'Erro ao duplicar projeto');
    } finally {
      setDuplicating(false);
    }
  };

  // Export handlers
  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await api.get(`/export/project/${id}/${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `projeto_${project?.name?.replace(/\s+/g, '_').substring(0, 20)}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Relatório ${format.toUpperCase()} exportado com sucesso!`);
    } catch (error) {
      logger.error('Error exporting:', error);
      toast.error('Erro ao exportar relatório');
    } finally {
      setExporting(false);
    }
  };

  // Task history handler
  const openTaskHistory = (task) => {
    setHistoryTask(task);
    setHistoryOpen(true);
  };

  // Delete phase handler
  const openDeletePhaseDialog = (phase) => {
    setPhaseToDelete(phase);
    setDeletePhaseOpen(true);
  };

  const handleDeletePhase = async () => {
    if (!phaseToDelete) return;
    
    try {
      await api.delete(`/phases/${phaseToDelete.phase_id}`);
      toast.success('Fase excluída com sucesso!');
      setDeletePhaseOpen(false);
      setPhaseToDelete(null);
      fetchProjectData();
    } catch (error) {
      logger.error('Error deleting phase:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir fase');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Não Iniciado': 'bg-gray-200 text-gray-700',
      'Not Started': 'bg-gray-200 text-gray-700',
      'Em Andamento': 'bg-blue-100 text-blue-700',
      'In Progress': 'bg-blue-100 text-blue-700',
      'Iniciado': 'bg-blue-100 text-blue-700',
      'Concluído': 'bg-green-100 text-green-700',
      'Completed': 'bg-green-100 text-green-700',
      'Em Espera': 'bg-yellow-100 text-yellow-700',
      'On Hold': 'bg-yellow-100 text-yellow-700',
      'not_started': 'bg-gray-200 text-gray-700',
      'In Progress': 'bg-blue-100 text-blue-700',
      'in_progress': 'bg-blue-100 text-blue-700',
      'Completed': 'bg-green-100 text-green-700',
      'completed': 'bg-green-100 text-green-700',
      'On Hold': 'bg-yellow-100 text-yellow-700',
      'On Hold - Agency': 'bg-orange-100 text-orange-700',
      'cancelled': 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-200 text-gray-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'not_started': 'Não Iniciada',
      'Not Started': 'Não Iniciado',
      'Não Iniciado': 'Não Iniciado',
      'in_progress': 'Em Andamento',
      'In Progress': 'Em Andamento',
      'Iniciado': 'Em Andamento',
      'Em Andamento': 'Em Andamento',
      'completed': 'Concluída',
      'Completed': 'Concluído',
      'Concluído': 'Concluído',
      'On Hold': 'Em Espera',
      'Em Espera': 'Em Espera',
      'cancelled': 'Cancelada',
      'Cancelado': 'Cancelado',
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
        </div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout user={user}>
        <div className="text-center py-12">
          <p className="text-gray-600">Projeto não encontrado</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Project Header */}
        <div className="flex items-start justify-between">
          <div>
            {project.project_number && (
              <span
                className="inline-block text-xs font-mono px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 mb-1.5"
                data-testid="project-detail-number"
              >
                {project.project_number}
              </span>
            )}
            <div className="flex items-center space-x-3">
              <h1 className="text-3xl font-semibold text-gray-900">{project.name}</h1>
              <Badge className={getStatusColor(project.status)}>
                {getStatusLabel(project.status)}
              </Badge>
            </div>
            <p className="text-gray-600 mt-1">{project.location} • {project.project_type}</p>
            <p className="text-sm text-gray-500 mt-1">Cliente: {project.client_name}</p>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Export buttons */}
            {canEdit && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" disabled={exporting} data-testid="export-button">
                    <Download className="w-4 h-4 mr-2" />
                    {exporting ? 'Exportando...' : 'Exportar'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48" align="end">
                  <div className="space-y-1">
                    <Button
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={() => handleExport('excel')}
                      disabled={exporting}
                      data-testid="export-excel-button"
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                      Excel (.xlsx)
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={() => handleExport('pdf')}
                      disabled={exporting}
                      data-testid="export-pdf-button"
                    >
                      <FileText className="w-4 h-4 mr-2 text-red-600" />
                      PDF
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            )}
            
            {canEdit && (
              <Select value={project.status} onValueChange={handleUpdateProjectStatus}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Não Iniciado">Não Iniciado</SelectItem>
                  <SelectItem value="Em Andamento">Em Andamento</SelectItem>
                  <SelectItem value="Em Espera">Em Espera - Aguarda Cliente</SelectItem>
                  <SelectItem value="On Hold - Agency">Pausado - Aguarda Análise Órgão</SelectItem>
                  <SelectItem value="Concluído">Concluído</SelectItem>
                </SelectContent>
              </Select>
            )}
            
            {canEdit && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="icon" data-testid="project-actions-menu">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                    </svg>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48" align="end">
                  <div className="space-y-1">
                    <Button
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={openEditProject}
                      data-testid="edit-project-button"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Editar Projeto
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={handleDuplicateProject}
                      disabled={duplicating}
                      data-testid="duplicate-project-button"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      {duplicating ? 'Duplicando...' : 'Duplicar Projeto'}
                    </Button>
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteProjectOpen(true)}
                        data-testid="delete-project-button"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Excluir Projeto
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>

        {/* Project Progress */}
        <Card className="p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Progresso Geral</h2>
            <div className="flex items-center gap-3">
              {canEdit && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleRecalculateProject}
                  disabled={recalculating}
                  data-testid="recalculate-project-button"
                  title="Recalcula pesos das fases/tarefas e o progresso geral do projeto"
                >
                  <svg className={`w-4 h-4 mr-2 ${recalculating ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {recalculating ? 'Recalculando...' : 'Recalcular'}
                </Button>
              )}
              <span className="text-2xl font-bold text-emerald-800">
                {project.overall_percentage?.toFixed(1) || 0}%
              </span>
            </div>
          </div>
          <Progress value={project.overall_percentage || 0} className="h-3" />
          <div className="flex justify-between mt-2 text-sm text-gray-600">
            <span>Início: {new Date(project.start_date).toLocaleDateString('pt-BR')}</span>
            <span>Previsão: {new Date(project.estimated_end_date).toLocaleDateString('pt-BR')}</span>
          </div>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="phases" className="space-y-4">
          <TabsList>
            <TabsTrigger value="phases">Fases</TabsTrigger>
            <TabsTrigger value="tasks">Tarefas</TabsTrigger>
            <TabsTrigger value="protocols" data-testid="protocols-tab">
              <CalendarDays className="w-4 h-4 mr-2" />
              Protocolo
            </TabsTrigger>
            <TabsTrigger value="arts" data-testid="arts-tab">
              <FileText className="w-4 h-4 mr-2" />
              ART's
            </TabsTrigger>
            <TabsTrigger value="licenses">Licenças</TabsTrigger>
            <TabsTrigger value="registrations" data-testid="registrations-tab">Matrículas</TabsTrigger>
            <TabsTrigger value="discussion" data-testid="discussion-tab">
              <MessageSquare className="w-4 h-4 mr-2" />
              Discussão
            </TabsTrigger>
          </TabsList>

          <TabsContent value="phases">
            <Card className="p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Fases do Projeto</h2>
                {canEdit && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setPhaseBlocksPickerOpen(true)}
                      data-testid="add-phase-blocks-button"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                      Adicionar Blocos
                    </Button>
                    <Dialog open={openPhaseDialog} onOpenChange={setOpenPhaseDialog}>
                      <DialogTrigger asChild>
                        <Button className="bg-emerald-800 hover:bg-emerald-900" data-testid="add-phase-button">
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Adicionar Fase
                        </Button>
                      </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Nova Fase</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreatePhase} className="space-y-4">
                        <div>
                          <Label htmlFor="phase_name">Nome da Fase</Label>
                          <Input
                            id="phase_name"
                            data-testid="phase-name-input"
                            value={phaseForm.name}
                            onChange={(e) => setPhaseForm({ ...phaseForm, name: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <Label>Peso (%)</Label>
                          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border rounded text-sm text-gray-600" data-testid="phase-weight-auto-info">
                            <span>⚙️ Distribuído automaticamente entre as fases</span>
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="phase_protocol_date">Data de Protocolo</Label>
                          <Input
                            id="phase_protocol_date"
                            data-testid="phase-protocol-date-input"
                            type="date"
                            value={phaseForm.protocol_date}
                            onChange={(e) => setPhaseForm({ ...phaseForm, protocol_date: e.target.value })}
                          />
                          <p className="text-xs text-gray-500 mt-1">Data será exibida no calendário de atividades</p>
                        </div>
                        <div className="flex justify-end space-x-3">
                          <Button type="button" variant="outline" onClick={() => setOpenPhaseDialog(false)}>
                            Cancelar
                          </Button>
                          <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900" data-testid="submit-phase-button">
                            Criar Fase
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                  </div>
                )}
              </div>

              {phases.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">Nenhuma fase cadastrada ainda</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {phases.map((phase) => (
                    <div key={phase.phase_id} className={`border rounded-lg p-4 ${phase.active === false ? 'border-gray-300 bg-gray-50 opacity-70' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <h3 className="font-semibold text-gray-900">{phase.name}</h3>
                          <span className="text-sm text-gray-500">Peso: {phase.weight}%</span>
                          {phase.active === false && (
                            <Badge className="bg-gray-200 text-gray-600">
                              Inativa
                            </Badge>
                          )}
                          {phase.protocol_date && (
                            <Badge className="bg-purple-100 text-purple-700">
                              📋 Protocolo: {new Date(phase.protocol_date).toLocaleDateString('pt-BR')}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-emerald-800">
                            {phase.completion_percentage?.toFixed(1) || 0}%
                          </span>
                          {canEdit && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openEditPhase(phase)}
                              data-testid={`edit-phase-${phase.phase_id}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </Button>
                          )}
                          {canEdit && (
                            <Dialog open={openTaskDialog && selectedPhase === phase.phase_id} onOpenChange={(open) => {
                              setOpenTaskDialog(open);
                              if (open) setSelectedPhase(phase.phase_id);
                            }}>
                              <DialogTrigger asChild>
                                <Button size="sm" variant="outline" data-testid={`add-task-${phase.phase_id}`}>
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                  </svg>
                                  Tarefa
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Nova Tarefa - {phase.name}</DialogTitle>
                                </DialogHeader>
                                <form onSubmit={handleCreateTask} className="space-y-4">
                                  <div>
                                    <Label htmlFor="task_name">Nome da Tarefa</Label>
                                    <Input
                                      id="task_name"
                                      data-testid="task-name-input"
                                      value={taskForm.name}
                                      onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
                                      required
                                    />
                                  </div>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <Label>Peso (%)</Label>
                                      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border rounded text-xs text-gray-600" data-testid="task-weight-auto-info">
                                        <span>⚙️ Calculado automaticamente</span>
                                      </div>
                                    </div>
                                    <div>
                                      <Label htmlFor="task_deadline">Prazo</Label>
                                      <Input
                                        id="task_deadline"
                                        data-testid="task-deadline-input"
                                        type="date"
                                        value={taskForm.deadline}
                                        onChange={(e) => setTaskForm({ ...taskForm, deadline: e.target.value })}
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <Label htmlFor="task_assigned">Responsável</Label>
                                    <Select
                                      value={taskForm.assigned_to}
                                      onValueChange={(value) => setTaskForm({ ...taskForm, assigned_to: value })}
                                    >
                                      <SelectTrigger data-testid="task-assigned-select">
                                        <SelectValue placeholder="Selecione o responsável" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {consultants.map((consultant) => (
                                          <SelectItem key={consultant.user_id} value={consultant.full_name}>
                                            {consultant.full_name} ({consultant.role === 'ADMIN' ? 'Admin' : 'Consultor'})
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <input
                                      type="checkbox"
                                      id="task_client_visible"
                                      data-testid="task-client-visible-checkbox"
                                      checked={taskForm.client_visible}
                                      onChange={(e) => setTaskForm({ ...taskForm, client_visible: e.target.checked })}
                                      className="h-4 w-4 rounded border-gray-300 text-emerald-800 focus:ring-emerald-800"
                                    />
                                    <Label htmlFor="task_client_visible" className="text-sm font-normal cursor-pointer">
                                      Visível para o cliente
                                    </Label>
                                  </div>
                                  <div className="flex justify-end space-x-3">
                                    <Button type="button" variant="outline" onClick={() => setOpenTaskDialog(false)}>
                                      Cancelar
                                    </Button>
                                    <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900" data-testid="submit-task-button">
                                      Criar Tarefa
                                    </Button>
                                  </div>
                                </form>
                              </DialogContent>
                            </Dialog>
                          )}
                          {canEdit && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => openDeletePhaseDialog(phase)}
                              data-testid={`delete-phase-${phase.phase_id}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </Button>
                          )}
                        </div>
                      </div>

                      <Progress value={phase.completion_percentage || 0} className="h-2 mb-4" />

                      {tasks[phase.phase_id]?.length > 0 ? (
                        <SortableTaskList
                          tasks={tasks[phase.phase_id]}
                          phaseId={phase.phase_id}
                          canEdit={canEdit}
                          getStatusColor={getStatusColor}
                          getStatusLabel={getStatusLabel}
                          onStatusChange={handleUpdateTaskStatus}
                          onComments={setTaskCommentsFor}
                          onTime={setTaskTimeFor}
                          timeSummary={taskTimeSummary}
                          onHistory={openTaskHistory}
                          onEdit={openEditTask}
                          onCancel={handleCancelTask}
                          onReorder={handleReorderTasks}
                        />
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-4">
                          Nenhuma tarefa cadastrada nesta fase
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Tasks Tab - Shows all tasks grouped by phase */}
          <TabsContent value="tasks">
            <Card className="p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Todas as Tarefas</h2>
              </div>

              {phases.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">Nenhuma fase cadastrada ainda</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Tarefa</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Fase</th>
                        <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">Status</th>
                        <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">Prazo</th>
                        <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">Responsável</th>
                        {canEdit && <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">Ações</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {phases.flatMap(phase => 
                        (tasks[phase.phase_id] || []).map(task => (
                          <tr key={task.task_id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4">
                              <span className={`font-medium ${task.status === 'cancelled' ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                {task.name}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-600">{phase.name}</td>
                            <td className="py-3 px-4 text-center">
                              <Badge className={getStatusColor(task.status)}>
                                {getStatusLabel(task.status)}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-center text-sm text-gray-600">
                              {task.deadline ? new Date(task.deadline).toLocaleDateString('pt-BR') : '-'}
                            </td>
                            <td className="py-3 px-4 text-center text-sm text-gray-600">
                              {task.assigned_to || '-'}
                            </td>
                            {canEdit && task.status !== 'cancelled' && (
                              <td className="py-3 px-4 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openEditTask(task)}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                  </Button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  {phases.every(phase => !tasks[phase.phase_id]?.length) && (
                    <p className="text-center py-8 text-gray-500">Nenhuma tarefa cadastrada</p>
                  )}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Protocols Tab */}
          <TabsContent value="protocols">
            <Card className="p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Datas de Protocolo</h2>
                  <p className="text-sm text-gray-500 mt-1">Gerencie as datas de protocolo das fases do projeto</p>
                </div>
              </div>

              {protocols.length === 0 ? (
                <div className="text-center py-12">
                  <CalendarDays className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Nenhuma fase cadastrada ainda</p>
                  <p className="text-sm text-gray-400 mt-1">Crie fases na aba "Fases" para definir datas de protocolo</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {protocols.map((protocol) => (
                    <div 
                      key={protocol.phase_id} 
                      className={`flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 ${!protocol.active ? 'bg-gray-100 opacity-60 border-gray-300' : 'border-gray-200'}`}
                      data-testid={`protocol-item-${protocol.phase_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${protocol.protocol_date ? 'bg-purple-100' : 'bg-gray-100'}`}>
                          <CalendarDays className={`w-6 h-6 ${protocol.protocol_date ? 'text-purple-600' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-900">{protocol.phase_name}</h4>
                            {!protocol.active && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-600 rounded">Inativa</span>
                            )}
                          </div>
                          {protocol.protocol_date ? (
                            <div className="space-y-1">
                              <p className="text-sm text-purple-600 font-medium">
                                📋 {new Date(protocol.protocol_date).toLocaleDateString('pt-BR', { 
                                  weekday: 'long', 
                                  day: 'numeric', 
                                  month: 'long', 
                                  year: 'numeric' 
                                })}
                              </p>
                              {protocol.protocol_number && (
                                <p className="text-sm text-gray-600">
                                  <span className="font-medium">Nº Protocolo:</span> {protocol.protocol_number}
                                </p>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400">Sem data de protocolo definida</p>
                          )}
                        </div>
                      </div>
                      
                      {canEdit && (
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => openEditProtocol(protocol)}
                            data-testid={`edit-protocol-${protocol.phase_id}`}
                          >
                            {protocol.protocol_date ? 'Alterar' : 'Definir'}
                          </Button>
                          {protocol.protocol_date && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => handleRemoveProtocol(protocol.phase_id)}
                              data-testid={`remove-protocol-${protocol.phase_id}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Summary info */}
                  <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-start gap-3">
                      <CalendarDays className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-purple-900">
                          {protocols.filter(p => p.protocol_date).length} de {protocols.length} fases com data de protocolo
                        </p>
                        <p className="text-xs text-purple-600 mt-1">
                          As datas de protocolo aparecem no Calendário de Atividades como eventos em roxo
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* ARTs Tab */}
          <TabsContent value="arts">
            <Card className="p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">ART's do Projeto</h2>
                  <p className="text-sm text-gray-500 mt-1">Anotações de Responsabilidade Técnica</p>
                </div>
                {canEdit && (
                  <Button onClick={openNewArt} className="bg-emerald-800 hover:bg-emerald-900" data-testid="add-art-button">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Adicionar ART
                  </Button>
                )}
              </div>

              {arts.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">Nenhuma ART cadastrada neste projeto</p>
                  <p className="text-xs text-gray-400 mt-1">Clique em "Adicionar ART" para criar uma nova</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {arts.map((art) => (
                    <div key={art.art_id} className={`p-4 rounded-lg border ${
                      art.status === 'expired' ? 'border-red-200 bg-red-50' :
                      art.status === 'expiring_soon' ? 'border-amber-200 bg-amber-50' :
                      'border-gray-200 bg-gray-50'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <span className="text-lg font-semibold text-gray-900">ART Nº {art.art_number}</span>
                            {getArtStatusBadge(art.status)}
                          </div>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Atividade:</span> {art.activity}
                          </p>
                          {art.issued_by && (
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Emitido por:</span> {art.issued_by}
                            </p>
                          )}
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Vencimento:</span> {new Date(art.expiration_date).toLocaleDateString('pt-BR')}
                          </p>
                          {art.notes && (
                            <p className="text-sm text-gray-500">
                              <span className="font-medium">Observações:</span> {art.notes}
                            </p>
                          )}
                        </div>
                        {canEdit && (
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditArt(art)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteArt(art.art_id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="licenses">
            <LicensesTab projectId={id} user={user} />
          </TabsContent>

          <TabsContent value="registrations">
            <RegistrationsTab projectId={id} canEdit={canEdit} />
          </TabsContent>

          <TabsContent value="discussion">
            <Card className="p-6 border border-gray-200">
              <Comments parentType="project" parentId={id} currentUser={user} />
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* ART Dialog */}
      <Dialog open={artDialogOpen} onOpenChange={setArtDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingArt ? 'Editar ART' : 'Nova ART'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveArt} className="space-y-4">
            <div>
              <Label htmlFor="art_number">Número da ART</Label>
              <Input
                id="art_number"
                value={artForm.art_number}
                onChange={(e) => setArtForm({ ...artForm, art_number: e.target.value })}
                placeholder="Ex: 123456789"
                required
              />
            </div>
            <div>
              <Label htmlFor="art_activity">Atividade Vinculada</Label>
              <Input
                id="art_activity"
                value={artForm.activity}
                onChange={(e) => setArtForm({ ...artForm, activity: e.target.value })}
                placeholder="Ex: Licenciamento Ambiental"
                required
              />
            </div>
            <div>
              <Label htmlFor="art_issued_by">Profissional Emissor</Label>
              <Input
                id="art_issued_by"
                value={artForm.issued_by || ''}
                onChange={(e) => setArtForm({ ...artForm, issued_by: e.target.value })}
                placeholder="Nome do profissional que emitiu a ART"
              />
            </div>
            <div>
              <Label htmlFor="art_expiration">Data de Vencimento</Label>
              <Input
                id="art_expiration"
                type="date"
                value={artForm.expiration_date}
                onChange={(e) => setArtForm({ ...artForm, expiration_date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="art_notes">Observações (opcional)</Label>
              <Textarea
                id="art_notes"
                value={artForm.notes}
                onChange={(e) => setArtForm({ ...artForm, notes: e.target.value })}
                placeholder="Observações adicionais..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setArtDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900">
                {editingArt ? 'Salvar Alterações' : 'Criar ART'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Protocol Dialog */}
      <Dialog open={editProtocolOpen} onOpenChange={setEditProtocolOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {protocolToEdit?.protocol_date ? 'Editar Protocolo' : 'Definir Protocolo'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateProtocol} className="space-y-4">
            <div>
              <Label>Fase</Label>
              <p className="text-lg font-medium text-gray-900 mt-1">{protocolToEdit?.phase_name}</p>
            </div>
            <div>
              <Label htmlFor="protocol_date">Data do Protocolo</Label>
              <Input
                id="protocol_date"
                type="date"
                value={protocolDateForm}
                onChange={(e) => setProtocolDateForm(e.target.value)}
                data-testid="protocol-date-input"
              />
              <p className="text-xs text-gray-500 mt-1">Esta data será exibida no Calendário de Atividades</p>
            </div>
            <div>
              <Label htmlFor="protocol_number">Número do Protocolo</Label>
              <Input
                id="protocol_number"
                type="text"
                placeholder="Ex: 2025/001234"
                value={protocolNumberForm}
                onChange={(e) => setProtocolNumberForm(e.target.value)}
                data-testid="protocol-number-input"
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => {
                setEditProtocolOpen(false);
                setProtocolToEdit(null);
              }}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900">
                Salvar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={editProjectOpen} onOpenChange={setEditProjectOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Projeto</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditProject} className="space-y-4">
            <div>
              <Label htmlFor="edit_project_name">Nome do Projeto</Label>
              <Input
                id="edit_project_name"
                value={editProjectForm.name || ''}
                onChange={(e) => setEditProjectForm({ ...editProjectForm, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_client_name">Nome do Cliente</Label>
              <Input
                id="edit_client_name"
                value={editProjectForm.client_name || ''}
                onChange={(e) => setEditProjectForm({ ...editProjectForm, client_name: e.target.value })}
                placeholder="Nome do cliente neste projeto"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_project_type">Tipo</Label>
                <Input
                  id="edit_project_type"
                  value={editProjectForm.project_type || ''}
                  onChange={(e) => setEditProjectForm({ ...editProjectForm, project_type: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit_project_location">Localização</Label>
                <Input
                  id="edit_project_location"
                  value={editProjectForm.location || ''}
                  onChange={(e) => setEditProjectForm({ ...editProjectForm, location: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_start_date">Data Início</Label>
                <Input
                  id="edit_start_date"
                  type="date"
                  value={editProjectForm.start_date || ''}
                  onChange={(e) => setEditProjectForm({ ...editProjectForm, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit_end_date">Previsão de Término</Label>
                <Input
                  id="edit_end_date"
                  type="date"
                  value={editProjectForm.estimated_end_date || ''}
                  onChange={(e) => setEditProjectForm({ ...editProjectForm, estimated_end_date: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="edit_status">Status</Label>
              <Select
                value={editProjectForm.status || ''}
                onValueChange={(value) => setEditProjectForm({ ...editProjectForm, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Não Iniciado">Não Iniciado</SelectItem>
                  <SelectItem value="Em Andamento">Em Andamento</SelectItem>
                  <SelectItem value="Em Espera">Em Espera - Aguarda Cliente</SelectItem>
                  <SelectItem value="On Hold - Agency">Pausado - Aguarda Análise Órgão</SelectItem>
                  <SelectItem value="Concluído">Concluído</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditProjectOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900">
                Salvar Alterações
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Project Dialog */}
      <Dialog open={deleteProjectOpen} onOpenChange={setDeleteProjectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Excluir Projeto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">
                <strong>Atenção!</strong> Esta ação é irreversível. Todos os dados relacionados serão permanentemente excluídos:
              </p>
              <ul className="text-red-700 text-sm mt-2 list-disc list-inside">
                <li>Fases e tarefas</li>
                <li>Licenças</li>
                <li>Alertas</li>
              </ul>
            </div>
            <div>
              <Label htmlFor="confirm_name">Digite o nome do projeto para confirmar: <strong>{project?.name}</strong></Label>
              <Input
                id="confirm_name"
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder="Nome do projeto"
                className="mt-2"
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => {
                setDeleteProjectOpen(false);
                setDeleteConfirmName('');
              }}>
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleDeleteProject}
                className="bg-red-600 hover:bg-red-700"
                disabled={deleteConfirmName !== project?.name}
              >
                Excluir Permanentemente
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={editTaskOpen} onOpenChange={setEditTaskOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Tarefa</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditTask} className="space-y-4">
            <div>
              <Label htmlFor="edit_task_name">Nome da Tarefa</Label>
              <Input
                id="edit_task_name"
                value={editTaskForm.name || ''}
                onChange={(e) => setEditTaskForm({ ...editTaskForm, name: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_task_deadline">Prazo</Label>
                <Input
                  id="edit_task_deadline"
                  type="date"
                  value={editTaskForm.deadline || ''}
                  onChange={(e) => setEditTaskForm({ ...editTaskForm, deadline: e.target.value })}
                />
              </div>
              <div>
                <Label>Peso (%)</Label>
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border rounded text-sm text-gray-600">
                  <span>⚙️ {editTaskForm.weight?.toFixed(1) || 0}% (automático)</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_task_status">Status</Label>
                <Select
                  value={editTaskForm.status || ''}
                  onValueChange={(value) => setEditTaskForm({ ...editTaskForm, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">Não Iniciada</SelectItem>
                    <SelectItem value="in_progress">Em Andamento</SelectItem>
                    <SelectItem value="completed">Concluída</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit_task_assigned">Responsável</Label>
                <Select
                  value={editTaskForm.assigned_to || ''}
                  onValueChange={(value) => setEditTaskForm({ ...editTaskForm, assigned_to: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o responsável" />
                  </SelectTrigger>
                  <SelectContent>
                    {consultants.map((consultant) => (
                      <SelectItem key={consultant.user_id} value={consultant.full_name}>
                        {consultant.full_name} ({consultant.role === 'ADMIN' ? 'Admin' : 'Consultor'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="edit_task_notes">Observações</Label>
              <Textarea
                id="edit_task_notes"
                value={editTaskForm.notes || ''}
                onChange={(e) => setEditTaskForm({ ...editTaskForm, notes: e.target.value })}
                rows={3}
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="edit_task_client_visible"
                checked={editTaskForm.client_visible ?? true}
                onChange={(e) => setEditTaskForm({ ...editTaskForm, client_visible: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-emerald-800 focus:ring-emerald-800"
              />
              <Label htmlFor="edit_task_client_visible" className="text-sm font-normal cursor-pointer">
                Visível para o cliente
              </Label>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditTaskOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900">
                Salvar Alterações
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={editClientOpen} onOpenChange={setEditClientOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Cliente</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditClient} className="space-y-4">
            <div>
              <Label htmlFor="edit_client_name">Nome do Cliente</Label>
              <Input
                id="edit_client_name"
                value={editClientForm.full_name || ''}
                onChange={(e) => setEditClientForm({ ...editClientForm, full_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_client_email">Email</Label>
              <Input
                id="edit_client_email"
                type="email"
                value={editClientForm.email || ''}
                onChange={(e) => setEditClientForm({ ...editClientForm, email: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_client_phone">Telefone</Label>
                <Input
                  id="edit_client_phone"
                  value={editClientForm.phone || ''}
                  onChange={(e) => setEditClientForm({ ...editClientForm, phone: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit_client_company">Empresa</Label>
                <Input
                  id="edit_client_company"
                  value={editClientForm.company_name || ''}
                  onChange={(e) => setEditClientForm({ ...editClientForm, company_name: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="edit_client_contact">Pessoa de Contato</Label>
              <Input
                id="edit_client_contact"
                value={editClientForm.contact_person || ''}
                onChange={(e) => setEditClientForm({ ...editClientForm, contact_person: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit_client_notes">Observações</Label>
              <Textarea
                id="edit_client_notes"
                value={editClientForm.notes || ''}
                onChange={(e) => setEditClientForm({ ...editClientForm, notes: e.target.value })}
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditClientOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900">
                Salvar Alterações
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Task History Dialog */}
      <TaskHistory
        taskId={historyTask?.task_id}
        taskName={historyTask?.name}
        isOpen={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
          setHistoryTask(null);
        }}
      />

      {/* Edit Phase Dialog */}
      <Dialog open={editPhaseOpen} onOpenChange={setEditPhaseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Fase</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditPhase} className="space-y-4">
            <div>
              <Label htmlFor="edit_phase_name">Nome da Fase</Label>
              <Input
                id="edit_phase_name"
                value={editPhaseForm.name || ''}
                onChange={(e) => setEditPhaseForm({ ...editPhaseForm, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Peso (%)</Label>
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border rounded text-sm text-gray-600">
                <span>⚙️ {editPhaseForm.weight?.toFixed(1) || 0}% (distribuído automaticamente)</span>
              </div>
            </div>
            <div>
              <Label htmlFor="edit_phase_protocol_date">Data de Protocolo</Label>
              <Input
                id="edit_phase_protocol_date"
                type="date"
                value={editPhaseForm.protocol_date || ''}
                onChange={(e) => setEditPhaseForm({ ...editPhaseForm, protocol_date: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">Data será exibida no calendário de atividades</p>
            </div>
            <div className="flex items-center space-x-3 pt-2 pb-2">
              <input
                type="checkbox"
                id="edit_phase_active"
                checked={editPhaseForm.active !== false}
                onChange={(e) => setEditPhaseForm({ ...editPhaseForm, active: e.target.checked })}
                className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
              />
              <Label htmlFor="edit_phase_active" className="text-sm font-medium text-gray-700 cursor-pointer">
                Fase ativa
              </Label>
            </div>
            {editPhaseForm.active === false && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-700">
                  Fases inativas não aparecem no calendário e são exibidas com estilo diferenciado.
                </p>
              </div>
            )}
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => {
                setEditPhaseOpen(false);
                setPhaseToEdit(null);
              }}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900">
                Salvar Alterações
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Phase Confirmation Dialog */}
      <Dialog open={deletePhaseOpen} onOpenChange={setDeletePhaseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Excluir Fase
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">
                <strong>Atenção!</strong> Esta ação é irreversível. A fase e todas as suas tarefas serão permanentemente excluídas.
              </p>
            </div>
            <p className="text-gray-600">
              Tem certeza que deseja excluir a fase <strong>"{phaseToDelete?.name}"</strong>?
            </p>
            {tasks[phaseToDelete?.phase_id]?.length > 0 && (
              <p className="text-sm text-orange-600">
                Esta fase possui {tasks[phaseToDelete?.phase_id]?.length} tarefa(s) que também serão excluídas.
              </p>
            )}
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => {
                setDeletePhaseOpen(false);
                setPhaseToDelete(null);
              }}>
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleDeletePhase}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Excluir Fase
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Phase Blocks Picker (multi-select modal) */}
      <PhaseBlocksPicker
        open={phaseBlocksPickerOpen}
        onOpenChange={setPhaseBlocksPickerOpen}
        projectId={id}
        onApplied={fetchProjectData}
      />

      {/* Task Comments Dialog */}
      <Dialog open={!!taskCommentsFor} onOpenChange={(o) => !o && setTaskCommentsFor(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="task-comments-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" style={{ color: '#0F6E56' }} />
              Comentários — {taskCommentsFor?.name || 'Tarefa'}
            </DialogTitle>
          </DialogHeader>
          {taskCommentsFor && (
            <Comments
              parentType="task"
              parentId={taskCommentsFor.task_id}
              currentUser={user}
              compact
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Task Time Entries Modal */}
      <TimeEntryModal
        open={!!taskTimeFor}
        onOpenChange={(o) => !o && setTaskTimeFor(null)}
        task={taskTimeFor}
        currentUser={user}
        onChange={async () => {
          // Refresh only the summary — no need to re-fetch tasks
          const allTaskIds = Object.values(tasks).flat().map((t) => t.task_id);
          if (allTaskIds.length === 0) return;
          try {
            const sum = await api.get('/time-entries/summary/by-task', {
              params: { task_ids: allTaskIds.join(',') },
            });
            setTaskTimeSummary(sum.data || {});
          } catch (err) {
            logger.error('Error refreshing time summary:', err);
          }
        }}
      />
    </Layout>
  );
}

export default ProjectDetails;
